import { myIcons } from "@/utils/icons";
import { AnimatePresence, motion } from "framer-motion";
import { t } from "i18next";
import { useRef, useState } from "react";

export const Skills = () => {
    const skillsTitleList = [
        t("electrical-title"),
        t("software-title"),
        t("design-title"),
    ];

    const categories = ["electronics", "software", "design"];

    const percentMap: Record<string, Record<string, number>> = {
        electronics: {
            tiaportal: 8,
            altium: 6,
            solidworks: 9,
            matlab: 6,
            stm32: 7,
            eplan: 5,
            autocad: 4,
            c: 8,
            factoryio: 9,
            proteus: 9,
            logic: 8,
            arduino: 8,
        },
        software: {
            js: 9,
            ts: 8,
            react: 9,
            flutter: 9,
            node: 6,
            html: 9,
            css: 9,
            github: 8,
            vscode: 8,
            chatgpt: 9,
            mysql: 8,
            php: 7,
            firebase: 5,
        },
        design: {
            figma: 9,
            davinci: 4
        }
    };


    return (
        <div>
            <p style={{ fontSize: "25px", marginBottom: "20px", color: 'var(--color-text)' }}>
                {t("skills-title")}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                {categories.map((category, idx) => {
                    const skills = Object.entries(t(category, { returnObjects: true }))
                        .map(([key, skill]: [string, any]) => ({
                            key,
                            ...skill,
                            percent: percentMap[category]?.[key] ?? 0 // buradan ekliyoruz
                        }));
                    return (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                backgroundColor: 'var(--color-bg-secondary)',
                                color: 'var(--color-text)',
                                flex: '1 1 300px',
                                minWidth: '250px',
                                padding: '20px',
                                gap: '10px',
                                borderRadius: '10px'
                            }}
                            key={category}
                        >
                            <p>{skillsTitleList[idx]}</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                {skills.map((skill, index) => {
                                    const icon =
                                        skill.key in myIcons.programs
                                            ? myIcons.programs[skill.key as keyof typeof myIcons.programs]
                                            : skill.key in myIcons.technologies
                                                ? myIcons.technologies[skill.key as keyof typeof myIcons.technologies]
                                                : skill.key in myIcons.socials
                                                    ? myIcons.socials[skill.key as keyof typeof myIcons.socials]
                                                    : "";

                                    return (
                                        <SkillsItem
                                            key={index}
                                            index={index}
                                            icon={icon}
                                            title={skill.title}
                                            percent={skill.percent}
                                            desc={skill.desc}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div >
    );
};

interface SkillsItemPropsI {
    icon: string;
    title: string;
    percent: number;
    desc: string[];
}

const SkillsItem = ({ icon, title, percent, desc, index }: SkillsItemPropsI & { index: number }) => {
    const [hovered, setHovered] = useState(false);
    const [cardPosition, setCardPosition] = useState<'left' | 'center' | 'right'>('center');
    const ref = useRef<HTMLDivElement>(null);

    const handleMouseEnter = () => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.left < 150) setCardPosition('left');
            else if (window.innerWidth - rect.right < 150) setCardPosition('right');
            else setCardPosition('center');
        }
        setHovered(true);
    };

    const isHighlight = icon == myIcons.programs.tiaportal || icon == myIcons.technologies.chatgpt || icon == myIcons.programs.solidworks || icon == myIcons.technologies.flutter;

    return (
        <motion.div
            initial={{ scale: 0 }}
            transition={{ duration: 0.4, delay: index * 0.15, ease: "easeOut" }}
            style={{ position: "relative", display: "flex" }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={() => setHovered(false)}
            ref={ref}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, amount: 1 }}
        >
            <motion.div
                animate={
                    isHighlight
                        ? {
                            background: [
                                "linear-gradient(var(--color-bg-secondary), var(--color-bg-secondary)) padding-box, linear-gradient(0deg, #ff00cc, #3333ff, #00ffcc, #ff00cc) border-box",
                                "linear-gradient(var(--color-bg-secondary), var(--color-bg-secondary)) padding-box, linear-gradient(360deg, #ff00cc, #3333ff, #00ffcc, #ff00cc) border-box",
                            ],
                        }
                        : {}
                }
                transition={
                    isHighlight
                        ? { duration: 2, repeat: Infinity, ease: "linear" }
                        : {}
                }
                style={{
                    display: "flex",
                    width: "50px",
                    height: "50px",
                    padding: "5px",
                    borderRadius: "5px",
                    alignItems: "center",
                    justifyContent: "center",
                    border: isHighlight ? "2px solid transparent" : "1px dashed #acacacff",
                    background: isHighlight
                        ? "linear-gradient(var(--color-bg-secondary), var(--color-bg-secondary)) padding-box, linear-gradient(90deg, #ff00cc, #3333ff, #00ffcc, #ff00cc) border-box"
                        : "var(--color-bg-secondary)",
                }}
            >
                <img
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    src={icon} alt="" />
            </motion.div>

            <AnimatePresence>
                {hovered && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: -20 }}
                        exit={{ opacity: 0, y: -10 }}
                        style={{
                            position: "absolute",
                            top: "-60px",
                            left: cardPosition === 'left' ? '-20px' : cardPosition === 'right' ? 'auto' : '-100px',
                            right: cardPosition === 'right' ? '-20px' : 'auto',
                            transform: cardPosition === 'center' ? "translateX(-50%)" : "none",
                            backgroundColor: 'var(--color-bg-secondary)',
                            padding: "10px",
                            borderRadius: "5px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                            whiteSpace: "normal",
                            zIndex: 10,
                        }}
                    >
                        <SkillsCard icon={icon} title={title} percent={percent} desc={desc} />
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

interface SkillsCardPropsI {
    icon: string;
    title: string;
    percent: number;
    desc: string[]
}

const SkillsCard = ({ icon, title, percent, desc }: SkillsCardPropsI) => {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <img style={{ width: '30px', height: '30px' }} src={icon} alt="" />
                <p>{title}</p>
            </div>
            <ul
                style={{
                    paddingLeft: '20px',
                    listStyleType: 'disc',
                    maxWidth: '250px',
                    whiteSpace: 'normal',
                    overflowWrap: 'break-word',
                }}
            >
                {desc.map((item, index) => (
                    <li
                        key={index}
                        style={{ fontSize: '12px' }}
                        dangerouslySetInnerHTML={{ __html: item }}
                    />
                ))}
            </ul>
            <div style={{ display: 'flex', gap: '5px' }}>
                {Array.from({ length: 10 }).map((_, index) => (
                    <motion.div
                        key={index}
                        initial={{ backgroundColor: '#ccc', scaleX: 0 }}
                        animate={{
                            backgroundColor: index < percent ? '#4caf50' : '#ccc',
                            scaleX: index < percent ? 1 : 1
                        }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        style={{
                            flex: 1,
                            height: '5px',
                            width: '20px',
                            backgroundColor: index < percent ? '#4caf50' : '#ccc',
                            borderRadius: '4px',
                        }}
                    />
                ))}
            </div>
        </div>
    );
};
